import { useEffect, useState } from "react";
import api from "../lib/api";
import {
  BarChart3, TrendingUp, Percent, ShoppingBag, Users, Package,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Line, ComposedChart, Cell,
} from "recharts";

const euro = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const CLIENT_COLORS = ["#E8823A", "#2563EB", "#34A853", "#F5B547", "#8B5CF6", "#EC4899", "#0EA5E9", "#14B8A6"];

export default function Statistics() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/statistics").then((r) => setS(r.data)).catch(() => {}); }, []);

  if (!s) return <div className="p-12 text-center text-slate-400">Chargement des statistiques…</div>;
  const maxClient = Math.max(1, ...s.ca_by_client.map((c) => c.ca));

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-navy flex items-center justify-center"><BarChart3 className="w-5 h-5 text-sky-400" /></div>
        <div>
          <h1 className="text-3xl font-display font-bold text-navy">Statistiques</h1>
          <p className="text-slate-500">CA par client, saisonnalité, top produits et performance commerciale</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={TrendingUp} color="emerald" label="CA total (HT)" value={euro(s.total_ca)} testid="stat-kpi-ca" />
        <Kpi icon={ShoppingBag} color="cognac" label="Panier moyen" value={euro(s.avg_basket)} testid="stat-kpi-basket" />
        <Kpi icon={Percent} color="sky" label="Taux de conversion" value={`${s.conversion} %`} sub={`${s.nb_signed}/${s.nb_devis} devis signés`} testid="stat-kpi-conv" />
        <Kpi icon={Users} color="violet" label="Factures émises" value={s.nb_factures} testid="stat-kpi-fact" />
      </div>

      {/* Saisonnalité */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="stat-monthly">
        <h3 className="font-display font-semibold text-navy text-lg mb-1">Saisonnalité — 12 derniers mois</h3>
        <p className="text-xs text-slate-400 mb-3">Chiffre d'affaires, dépenses et marge par mois</p>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={s.monthly} margin={{ left: -10, right: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v) => euro(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
            <Bar dataKey="ca" name="CA" fill="#2563EB" radius={[4, 4, 0, 0]} maxBarSize={26} />
            <Bar dataKey="depenses" name="Dépenses" fill="#E8823A" radius={[4, 4, 0, 0]} maxBarSize={26} />
            <Line dataKey="marge" name="Marge" stroke="#34A853" strokeWidth={2.5} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* CA par client */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="stat-clients">
          <h3 className="font-display font-semibold text-navy text-lg mb-3 inline-flex items-center gap-2"><Users className="w-5 h-5 text-cognac-500" /> Top clients (CA HT)</h3>
          {s.ca_by_client.length > 0 ? (
            <div className="space-y-3">
              {s.ca_by_client.map((c, i) => (
                <div key={c.client} data-testid={`stat-client-${i}`}>
                  <div className="flex items-center justify-between text-sm mb-1"><span className="text-slate-600 truncate">{c.client}</span><span className="font-semibold text-navy tabular">{euro(c.ca)}</span></div>
                  <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(c.ca / maxClient) * 100}%`, background: CLIENT_COLORS[i % CLIENT_COLORS.length] }} /></div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400 py-8 text-center">Aucune facture ou devis signé pour l'instant.</p>}
        </div>

        {/* Top produits */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="stat-products">
          <h3 className="font-display font-semibold text-navy text-lg mb-3 inline-flex items-center gap-2"><Package className="w-5 h-5 text-cognac-500" /> Top produits & services</h3>
          {s.top_products.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100"><th className="py-2 font-medium">Désignation</th><th className="py-2 font-medium text-right">Qté</th><th className="py-2 font-medium text-right">CA HT</th></tr></thead>
                <tbody>
                  {s.top_products.map((p, i) => (
                    <tr key={i} className="border-b border-slate-50" data-testid={`stat-product-${i}`}>
                      <td className="py-2 text-slate-700 pr-2">{p.description}</td>
                      <td className="py-2 text-right text-slate-500 tabular">{p.qty}</td>
                      <td className="py-2 text-right font-semibold text-navy tabular">{euro(p.ca)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-sm text-slate-400 py-8 text-center">Aucune ligne de vente pour l'instant.</p>}
        </div>
      </div>
    </div>
  );
}

const CLR = {
  sky: { bg: "bg-sky-50", fg: "text-sky-600" },
  emerald: { bg: "bg-emerald-50", fg: "text-emerald-600" },
  cognac: { bg: "bg-cognac-50", fg: "text-cognac-600" },
  violet: { bg: "bg-violet-50", fg: "text-violet-600" },
};

const Kpi = ({ icon: Icon, color, label, value, sub, testid }) => {
  const c = CLR[color] || CLR.sky;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={testid}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${c.bg} mb-2`}><Icon className={`w-5 h-5 ${c.fg}`} /></div>
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="text-2xl font-display font-bold text-navy tabular">{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
};
